// Bloques B/C/E/G-S de 018A (Fase 1B, Tanda 14, ver PLAN.md). 3 componentes,
// uno por punto de inserción en el JSX de `Formulario018A.tsx` (B/C van antes
// de D, E entre D y F, G-S después de F) — mismo criterio de orden que el
// formulario oficial, no alfabético de archivos.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import { SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { imcTexto } from './calculosClinicosDatos';
import {
  APGAR_OPCIONES,
  FACTORES_RH,
  GRUPOS_SANGUINEOS,
  CAMPOS_PERDIDAS,
  CAMPOS_REPOSICION,
  CONDUCIDO_A_OPCIONES,
  EXTUBADO_INTUBADO_OPCIONES,
  FILAS_ACCESOS_VASCULARES,
  INCIDENTES_OPCIONES,
  PRIORIDAD_OPCIONES,
  REGION_OPERATORIA_OPCIONES,
  TECNICAS_ESPECIALES_OPCIONES,
  TEMPERATURA_OPCIONES,
  TOPE_FILAS_DROGAS,
  TOPE_FILAS_LABORATORIO,
  filaDrogaVacia,
  filaLaboratorioVacia,
  type AccesoVascular,
  type CampoPerdidas,
  type CampoReposicion,
  type ConducidoA,
  type ConstantesEgreso,
  type EstadoBloqueA,
  type EstadoBloqueB,
  type FactorRh,
  type GrupoSanguineo,
  type EstadoBloquesRestantes018A,
  type ExtubadoIntubado,
  type FilaAccesoVascular,
  type FilaDroga,
  type FilaLaboratorio,
  type PrioridadCirugia,
} from './bloquesRestantes018ADatos';

function CampoTexto({ id, label, value, onChange, soloLectura = false }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void; readonly soloLectura?: boolean }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} soloLectura={soloLectura} onChange={onChange} />;
}

function CampoNumero({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function CampoSelect<T extends string>({
  id,
  label,
  value,
  opciones,
  etiquetaOpcion,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: T | '';
  readonly opciones: readonly T[];
  readonly etiquetaOpcion: (op: T) => string;
  readonly onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((op) => (
            <SelectItem key={op} value={op}>
              {etiquetaOpcion(op)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface BloquesProps {
  readonly estado: EstadoBloquesRestantes018A;
  readonly onChange: (estado: EstadoBloquesRestantes018A) => void;
}

// ---------- B + C (antes de D) ----------

interface Bloque018ABCProps extends BloquesProps {
  readonly diagnosticoPreoperatorio: DiagnosticoCie10Valor;
  readonly onChangeDiagnosticoPreoperatorio: (v: DiagnosticoCie10Valor) => void;
  readonly diagnosticoPostoperatorio: DiagnosticoCie10Valor;
  readonly onChangeDiagnosticoPostoperatorio: (v: DiagnosticoCie10Valor) => void;
}

export function Bloque018ABC({ estado, onChange, diagnosticoPreoperatorio, onChangeDiagnosticoPreoperatorio, diagnosticoPostoperatorio, onChangeDiagnosticoPostoperatorio }: Bloque018ABCProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarB(cambios: Partial<EstadoBloqueB>): void {
    onChange({ ...estado, b: { ...estado.b, ...cambios } });
  }
  function actualizarA(cambios: Partial<EstadoBloqueA>): void {
    onChange({ ...estado, a: { ...estado.a, ...cambios } });
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueA.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="f018a-a-fecha">{t('formularios.f018a.bloqueA.fecha')}</Label>
            <Input id="f018a-a-fecha" type="date" value={estado.a.fecha} onChange={(e) => actualizarA({ fecha: e.target.value })} />
          </div>
          <CampoNumero id="f018a-a-talla" label={t('formularios.f018a.bloqueA.talla')} value={estado.a.talla} onChange={(v) => actualizarA({ talla: v })} />
          <CampoNumero id="f018a-a-peso" label={t('formularios.f018a.bloqueA.peso')} value={estado.a.peso} onChange={(v) => actualizarA({ peso: v })} />
          <CampoTexto id="f018a-a-imc" label={t('formularios.f018a.bloqueA.imc')} value={imcTexto(estado.a.peso, estado.a.talla)} onChange={() => undefined} soloLectura />
          <CampoSelect<GrupoSanguineo> id="f018a-a-grupo" label={t('formularios.f018a.bloqueA.grupo')} value={estado.a.grupo} opciones={GRUPOS_SANGUINEOS} etiquetaOpcion={(op) => op} onChange={(v) => actualizarA({ grupo: v })} />
          <CampoSelect<FactorRh> id="f018a-a-factor" label={t('formularios.f018a.bloqueA.factor')} value={estado.a.factor} opciones={FACTORES_RH} etiquetaOpcion={(op) => `Rh ${op}`} onChange={(v) => actualizarA({ factor: v })} />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueB.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectorDiagnosticoCie10 label={t('formularios.f018a.bloqueB.diagnosticoPreoperatorio')} value={diagnosticoPreoperatorio} onChange={onChangeDiagnosticoPreoperatorio} />
          <CampoTexto id="f018a-b-diag-preop-adicional" label={t('formularios.f018a.bloqueB.diagnosticoPreoperatorioAdicional')} value={estado.b.diagnosticoPreoperatorioAdicional} onChange={(v) => actualizarB({ diagnosticoPreoperatorioAdicional: v })} />
          <CampoTexto id="f018a-b-cirugia-propuesta" label={t('formularios.f018a.bloqueB.cirugiaPropuesta')} value={estado.b.cirugiaPropuesta} onChange={(v) => actualizarB({ cirugiaPropuesta: v })} />
          <CampoTexto id="f018a-b-cirugia-propuesta-adicional" label={t('formularios.f018a.bloqueB.cirugiaPropuestaAdicional')} value={estado.b.cirugiaPropuestaAdicional} onChange={(v) => actualizarB({ cirugiaPropuestaAdicional: v })} />
          <CampoTexto id="f018a-b-especialidad-servicio" label={t('formularios.f018a.bloqueB.especialidadServicio')} value={estado.b.especialidadServicio} onChange={(v) => actualizarB({ especialidadServicio: v })} />
          <CampoSelect<PrioridadCirugia>
            id="f018a-b-prioridad"
            label={t('formularios.f018a.bloqueB.prioridad')}
            value={estado.b.prioridad}
            opciones={PRIORIDAD_OPCIONES}
            etiquetaOpcion={(op) => t(`formularios.f018a.bloqueB.prioridadOpciones.${op}`)}
            onChange={(v) => actualizarB({ prioridad: v })}
          />
          <SelectorDiagnosticoCie10 label={t('formularios.f018a.bloqueB.diagnosticoPostoperatorio')} value={diagnosticoPostoperatorio} onChange={onChangeDiagnosticoPostoperatorio} />
          <CampoTexto id="f018a-b-diag-postop-adicional" label={t('formularios.f018a.bloqueB.diagnosticoPostoperatorioAdicional')} value={estado.b.diagnosticoPostoperatorioAdicional} onChange={(v) => actualizarB({ diagnosticoPostoperatorioAdicional: v })} />
          <CampoTexto id="f018a-b-cirugia-realizada" label={t('formularios.f018a.bloqueB.cirugiaRealizada')} value={estado.b.cirugiaRealizada} onChange={(v) => actualizarB({ cirugiaRealizada: v })} />
          <CampoTexto id="f018a-b-cirugia-realizada-adicional" label={t('formularios.f018a.bloqueB.cirugiaRealizadaAdicional')} value={estado.b.cirugiaRealizadaAdicional} onChange={(v) => actualizarB({ cirugiaRealizadaAdicional: v })} />
          <CampoTexto id="f018a-b-anestesiologo" label={t('formularios.f018a.bloqueB.anestesiologo')} value={estado.b.anestesiologo} onChange={(v) => actualizarB({ anestesiologo: v })} />
          <CampoTexto id="f018a-b-ayudantes" label={t('formularios.f018a.bloqueB.ayudantes')} value={estado.b.ayudantes} onChange={(v) => actualizarB({ ayudantes: v })} />
          <CampoTexto id="f018a-b-ayudantes2" label={t('formularios.f018a.bloqueB.ayudantes2')} value={estado.b.ayudantes2} onChange={(v) => actualizarB({ ayudantes2: v })} />
          <CampoTexto id="f018a-b-instrumentista" label={t('formularios.f018a.bloqueB.instrumentista')} value={estado.b.instrumentista} onChange={(v) => actualizarB({ instrumentista: v })} />
          <CampoTexto id="f018a-b-quirofano" label={t('formularios.f018a.bloqueB.quirofano')} value={estado.b.quirofano} onChange={(v) => actualizarB({ quirofano: v })} />
          <CampoTexto id="f018a-b-cirujano" label={t('formularios.f018a.bloqueB.cirujano')} value={estado.b.cirujano} onChange={(v) => actualizarB({ cirujano: v })} />
          <CampoTexto id="f018a-b-circulante" label={t('formularios.f018a.bloqueB.circulante')} value={estado.b.circulante} onChange={(v) => actualizarB({ circulante: v })} />
          <CampoTexto id="f018a-b-otros" label={t('formularios.f018a.bloqueB.otros')} value={estado.b.otros} onChange={(v) => actualizarB({ otros: v })} />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueC.titulo')}</h4>
        <ChecklistGrid claves={REGION_OPERATORIA_OPCIONES} prefijoI18n="formularios.f018a.bloqueC.region.opciones" prefijoId="f018a-c" valores={estado.regionOperatoria} onChange={(v) => onChange({ ...estado, regionOperatoria: v })} />
      </div>
    </div>
  );
}

// ---------- E (entre D y F) ----------

export function Bloque018AE({ estado, onChange }: BloquesProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarFila(id: string, campo: keyof Omit<FilaDroga, 'id'>, valor: string): void {
    onChange({ ...estado, drogas: estado.drogas.map((f) => (f.id === id ? { ...f, [campo]: valor } : f)) });
  }

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueE.titulo')}</h4>
      <div className="flex flex-col gap-2">
        {estado.drogas.map((f) => (
          <div key={f.id} className="grid grid-cols-1 gap-2 rounded-md border border-border p-3 sm:grid-cols-5 sm:items-end">
            <CampoTexto id={`f018a-e-${f.id}-hora`} label={t('formularios.f018a.bloqueE.hora')} value={f.hora} onChange={(v) => actualizarFila(f.id, 'hora', v)} />
            <CampoTexto id={`f018a-e-${f.id}-droga`} label={t('formularios.f018a.bloqueE.droga')} value={f.droga} onChange={(v) => actualizarFila(f.id, 'droga', v)} />
            <CampoTexto id={`f018a-e-${f.id}-dosis`} label={t('formularios.f018a.bloqueE.dosis')} value={f.dosis} onChange={(v) => actualizarFila(f.id, 'dosis', v)} />
            <CampoTexto id={`f018a-e-${f.id}-via`} label={t('formularios.f018a.bloqueE.via')} value={f.via} onChange={(v) => actualizarFila(f.id, 'via', v)} />
            <Button type="button" variant="ghost" size="sm" className="w-fit" onClick={() => onChange({ ...estado, drogas: estado.drogas.filter((row) => row.id !== f.id) })}>
              <Trash2 className="size-4" /> {t('formularios.f018a.bloqueE.quitarFila')}
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={estado.drogas.length >= TOPE_FILAS_DROGAS} onClick={() => onChange({ ...estado, drogas: [...estado.drogas, filaDrogaVacia()] })}>
          {t('formularios.f018a.bloqueE.agregarFila')}
        </Button>
      </div>
    </div>
  );
}

// ---------- G a S (después de F) ----------

function BloqueG({ estado, onChange }: BloquesProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarAcceso(fila: FilaAccesoVascular, cambios: Partial<AccesoVascular>): void {
    onChange({ ...estado, accesosVasculares: { ...estado.accesosVasculares, [fila]: { ...estado.accesosVasculares[fila], ...cambios } } });
  }

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueG.titulo')}</h4>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr>
              <th className="p-2 text-left text-xs font-medium text-hce-muted">{t('formularios.f018a.bloqueG.fila')}</th>
              <th className="p-2 text-left text-xs font-medium text-hce-muted">{t('formularios.f018a.bloqueG.tipo')}</th>
              <th className="p-2 text-left text-xs font-medium text-hce-muted">{t('formularios.f018a.bloqueG.calibre')}</th>
              <th className="p-2 text-left text-xs font-medium text-hce-muted">{t('formularios.f018a.bloqueG.sitio')}</th>
            </tr>
          </thead>
          <tbody>
            {FILAS_ACCESOS_VASCULARES.map((fila) => (
              <tr key={fila} className="border-t border-border">
                <td className="p-2 text-xs text-hce-ink">{t(`formularios.f018a.bloqueG.filas.${fila}`)}</td>
                <td className="p-2">
                  <Input aria-label={t('formularios.f018a.bloqueG.tipo')} value={estado.accesosVasculares[fila].tipo} onChange={(e) => actualizarAcceso(fila, { tipo: e.target.value })} />
                </td>
                <td className="p-2">
                  <Input aria-label={t('formularios.f018a.bloqueG.calibre')} value={estado.accesosVasculares[fila].calibre} onChange={(e) => actualizarAcceso(fila, { calibre: e.target.value })} />
                </td>
                <td className="p-2">
                  <Input aria-label={t('formularios.f018a.bloqueG.sitio')} value={estado.accesosVasculares[fila].sitio} onChange={(e) => actualizarAcceso(fila, { sitio: e.target.value })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BloqueHI({ estado, onChange }: BloquesProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarReposicion(campo: CampoReposicion, valor: string): void {
    onChange({ ...estado, reposicion: { ...estado.reposicion, [campo]: valor } });
  }
  function actualizarPerdidas(campo: CampoPerdidas, valor: string): void {
    onChange({ ...estado, perdidas: { ...estado.perdidas, [campo]: valor } });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueH.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {CAMPOS_REPOSICION.map((campo) => (
            <CampoNumero key={campo} id={`f018a-h-${campo}`} label={t(`formularios.f018a.bloqueH.campos.${campo}`)} value={estado.reposicion[campo]} onChange={(v) => actualizarReposicion(campo, v)} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueI.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {CAMPOS_PERDIDAS.map((campo) => (
            <CampoNumero key={campo} id={`f018a-i-${campo}`} label={t(`formularios.f018a.bloqueI.campos.${campo}`)} value={estado.perdidas[campo]} onChange={(v) => actualizarPerdidas(campo, v)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function BloqueJK({ estado, onChange }: BloquesProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueJ.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="flex items-center gap-2 self-end pb-2">
            <Checkbox id="f018a-j-feto-muerto" checked={estado.fetoMuerto} onCheckedChange={(v) => onChange({ ...estado, fetoMuerto: v === true })} />
            <Label htmlFor="f018a-j-feto-muerto" className="font-normal">
              {t('formularios.f018a.bloqueJ.fetoMuerto')}
            </Label>
          </div>
          <CampoSelect id="f018a-j-apgar1" label={t('formularios.f018a.bloqueJ.apgar1')} value={estado.apgar1} opciones={APGAR_OPCIONES} etiquetaOpcion={(op) => op} onChange={(v) => onChange({ ...estado, apgar1: v })} />
          <CampoSelect id="f018a-j-apgar5" label={t('formularios.f018a.bloqueJ.apgar5')} value={estado.apgar5} opciones={APGAR_OPCIONES} etiquetaOpcion={(op) => op} onChange={(v) => onChange({ ...estado, apgar5: v })} />
          <CampoSelect id="f018a-j-apgar10" label={t('formularios.f018a.bloqueJ.apgar10')} value={estado.apgar10} opciones={APGAR_OPCIONES} etiquetaOpcion={(op) => op} onChange={(v) => onChange({ ...estado, apgar10: v })} />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueK.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CampoTexto id="f018a-k-duracion-anestesia" label={t('formularios.f018a.bloqueK.duracionAnestesia')} value={estado.duracionAnestesia} onChange={(v) => onChange({ ...estado, duracionAnestesia: v })} />
          <CampoTexto id="f018a-k-duracion-cirugia" label={t('formularios.f018a.bloqueK.duracionCirugia')} value={estado.duracionCirugia} onChange={(v) => onChange({ ...estado, duracionCirugia: v })} />
        </div>
      </div>
    </div>
  );
}

function BloqueLMN({ estado, onChange }: BloquesProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueL.titulo')}</h4>
        <ChecklistGrid claves={TECNICAS_ESPECIALES_OPCIONES} prefijoI18n="formularios.f018a.bloqueL.tecnicas.opciones" prefijoId="f018a-l" valores={estado.tecnicasEspeciales} onChange={(v) => onChange({ ...estado, tecnicasEspeciales: v })} />
      </div>
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueM.titulo')}</h4>
        <ChecklistGrid claves={TEMPERATURA_OPCIONES} prefijoI18n="formularios.f018a.bloqueM.mantenimiento.opciones" prefijoId="f018a-m" valores={estado.temperatura} onChange={(v) => onChange({ ...estado, temperatura: v })} />
        <CampoTexto id="f018a-m-otros" label={t('formularios.f018a.bloqueM.otros')} value={estado.temperaturaOtros} onChange={(v) => onChange({ ...estado, temperaturaOtros: v })} />
      </div>
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueN.titulo')}</h4>
        <ChecklistGrid claves={INCIDENTES_OPCIONES} prefijoI18n="formularios.f018a.bloqueN.incidentes.opciones" prefijoId="f018a-n" valores={estado.incidentes} onChange={(v) => onChange({ ...estado, incidentes: v })} />
      </div>
    </div>
  );
}

function BloqueO({ estado, onChange }: BloquesProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarFila(id: string, campo: keyof Omit<FilaLaboratorio, 'id'>, valor: string): void {
    onChange({ ...estado, laboratorio: estado.laboratorio.map((f) => (f.id === id ? { ...f, [campo]: valor } : f)) });
  }

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueO.titulo')}</h4>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr>
              {(['hora', 'ph', 'po2', 'pco2', 'hco3', 'eb', 'sato2', 'lactato', 'glucosa', 'na', 'k', 'cl', 'hcto', 'hb', 'otro'] as const).map((columna) => (
                <th key={columna} className="min-w-[90px] p-2 text-left text-xs font-medium text-hce-muted">
                  {t(`formularios.f018a.bloqueO.columnas.${columna}`)}
                </th>
              ))}
              <th className="p-2">
                <span className="sr-only">{t('formularios.f018a.bloqueO.quitarFila')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {estado.laboratorio.map((f) => (
              <tr key={f.id} className="border-t border-border">
                {(['hora', 'ph', 'po2', 'pco2', 'hco3', 'eb', 'sato2', 'lactato', 'glucosa', 'na', 'k', 'cl', 'hcto', 'hb', 'otro'] as const).map((columna) => (
                  <td key={columna} className="p-2">
                    <Input aria-label={t(`formularios.f018a.bloqueO.columnas.${columna}`)} value={f[columna]} onChange={(e) => actualizarFila(f.id, columna, e.target.value)} />
                  </td>
                ))}
                <td className="p-2">
                  <button type="button" aria-label={t('formularios.f018a.bloqueO.quitarFila')} onClick={() => onChange({ ...estado, laboratorio: estado.laboratorio.filter((row) => row.id !== f.id) })}>
                    <Trash2 className="size-4 text-hce-muted" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-fit"
        disabled={estado.laboratorio.length >= TOPE_FILAS_LABORATORIO}
        onClick={() => onChange({ ...estado, laboratorio: [...estado.laboratorio, filaLaboratorioVacia()] })}
      >
        {t('formularios.f018a.bloqueO.agregarFila')}
      </Button>
    </div>
  );
}

function BloqueP({ estado, onChange }: BloquesProps): JSX.Element {
  const { t } = useTranslation();
  return <AreaDictado id="f018a-p-observaciones" etiqueta={t('formularios.f018a.bloqueP.observaciones')} valor={estado.observaciones} onChange={(v) => onChange({ ...estado, observaciones: v })} />;
}

function BloqueRS({ estado, onChange }: BloquesProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarConstantes(cambios: Partial<ConstantesEgreso>): void {
    onChange({ ...estado, constantesEgreso: { ...estado.constantesEgreso, ...cambios } });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueR.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CampoSelect<ExtubadoIntubado>
            id="f018a-r-extubado-intubado"
            label={t('formularios.f018a.bloqueR.extubadoIntubado')}
            value={estado.extubadoIntubado}
            opciones={EXTUBADO_INTUBADO_OPCIONES}
            etiquetaOpcion={(op) => t(`formularios.f018a.bloqueR.extubadoIntubadoOpciones.${op}`)}
            onChange={(v) => onChange({ ...estado, extubadoIntubado: v })}
          />
          <CampoSelect<ConducidoA>
            id="f018a-r-conducido-a"
            label={t('formularios.f018a.bloqueR.conducidoA')}
            value={estado.conducidoA}
            opciones={CONDUCIDO_A_OPCIONES}
            etiquetaOpcion={(op) => t(`formularios.f018a.bloqueR.conducidoAOpciones.${op}`)}
            onChange={(v) => onChange({ ...estado, conducidoA: v })}
          />
        </div>
        <Label>{t('formularios.f018a.bloqueR.constantesEgreso')}</Label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
          <CampoTexto id="f018a-r-ta" label={t('formularios.f018a.bloqueR.ta')} value={estado.constantesEgreso.ta} onChange={(v) => actualizarConstantes({ ta: v })} />
          <CampoTexto id="f018a-r-fc" label={t('formularios.f018a.bloqueR.fc')} value={estado.constantesEgreso.fc} onChange={(v) => actualizarConstantes({ fc: v })} />
          <CampoTexto id="f018a-r-fr" label={t('formularios.f018a.bloqueR.fr')} value={estado.constantesEgreso.fr} onChange={(v) => actualizarConstantes({ fr: v })} />
          <CampoTexto id="f018a-r-sato2" label={t('formularios.f018a.bloqueR.sato2')} value={estado.constantesEgreso.sato2} onChange={(v) => actualizarConstantes({ sato2: v })} />
          <CampoTexto id="f018a-r-temperatura" label={t('formularios.f018a.bloqueR.temperatura')} value={estado.constantesEgreso.temperatura} onChange={(v) => actualizarConstantes({ temperatura: v })} />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f018a.bloqueS.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <CampoTexto id="f018a-s-hora" label={t('formularios.f018a.bloqueS.hora')} value={estado.responsableHora} onChange={(v) => onChange({ ...estado, responsableHora: v })} />
          <CampoTexto id="f018a-s-nombre" label={t('formularios.f018a.bloqueS.nombre')} value={estado.responsableNombre} onChange={(v) => onChange({ ...estado, responsableNombre: v })} />
          <CampoTexto id="f018a-s-codigo" label={t('formularios.f018a.bloqueS.codigo')} value={estado.responsableCodigo} onChange={(v) => onChange({ ...estado, responsableCodigo: v })} />
        </div>
      </div>
    </div>
  );
}

export function Bloque018AGaS({ estado, onChange }: BloquesProps): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <BloqueG estado={estado} onChange={onChange} />
      <BloqueHI estado={estado} onChange={onChange} />
      <BloqueJK estado={estado} onChange={onChange} />
      <BloqueLMN estado={estado} onChange={onChange} />
      <BloqueO estado={estado} onChange={onChange} />
      <BloqueP estado={estado} onChange={onChange} />
      <BloqueRS estado={estado} onChange={onChange} />
    </div>
  );
}
